import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, X, MapPin } from 'lucide-react';
import { api } from '../../lib/api.js';
import { useMapStore } from '../../store/mapStore.js';

interface Suggestion {
  id: string;
  name: string;
  lat: number;
  lng: number;
  categorySlug?: string;
  categoryIcon?: string;
  isGeocode?: boolean;
}

export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();
  const setCenter = useMapStore((s) => s.setCenter);
  const setZoom = useMapStore((s) => s.setZoom);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [placesResult, geocodeResult] = await Promise.all([
          api.search.autocomplete(query),
          api.search.geocode(query).catch(() => ({ data: [] as unknown[] })),
        ]);

        const placeSuggestions: Suggestion[] = placesResult.data.map((p) => ({
          id: p.id,
          name: p.name,
          lat: p.lat,
          lng: p.lng,
        }));

        const geocodeSuggestions: Suggestion[] = (geocodeResult.data as Array<{
          lat: number; lon: number; display_name: string; place_id: string;
        }>).slice(0, 3).map((g) => ({
          id: `geo_${g.place_id}`,
          name: g.display_name.split(',')[0],
          lat: g.lat,
          lng: g.lon,
          isGeocode: true,
        }));

        setSuggestions([...placeSuggestions, ...geocodeSuggestions]);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleSuggestionClick = (s: Suggestion) => {
    setShowSuggestions(false);
    setQuery(s.name);
    if (s.isGeocode) {
      setCenter(s.lat, s.lng);
      setZoom(16);
    } else {
      navigate(`/place/${s.id}`);
    }
  };

  return (
    <div className="search-bar">
      <Search size={20} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
      <form onSubmit={handleSubmit} className="flex-1">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search places, addresses..."
          className="w-full outline-none text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent"
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          aria-label="Search for places and addresses"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="search-suggestions"
        />
      </form>
      {query && (
        <button
          onClick={() => {
            setQuery('');
            setSuggestions([]);
          }}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X size={18} />
        </button>
      )}
      <AnimatePresence>
      {loading && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="absolute top-full left-0 right-0 mt-2 map-chrome p-3 text-sm text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-2"><span className="h-2 w-2 animate-pulse rounded-full bg-brand-500" /> Searching nearby...</span>
        </motion.div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }} className="absolute top-full left-0 right-0 mt-2 map-chrome overflow-hidden" id="search-suggestions" role="listbox">
          {suggestions.map((s) => (
            <button
              key={s.id}
              onClick={() => handleSuggestionClick(s)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-50 dark:border-gray-700 last:border-0 flex items-center gap-2"
            >
              {s.isGeocode && <MapPin size={14} className="text-gray-400 flex-shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate">{s.name}</div>
                {s.isGeocode && (
                  <div className="text-xs text-gray-400 dark:text-gray-500">Location</div>
                )}
              </div>
            </button>
          ))}
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}
